import { AnimatePresence } from 'motion/react';
import React, { useEffect, useState } from 'react';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import { useMagneticWallLayout } from '../MagneticWallLayout';
import { AnnotationPreviewContainer } from '../annotation/Annotation';
import AnnotationIcon from '../annotation/AnnotationIcon';
import AnnotationLink from '../annotation/AnnotationLink';
import AnnotationPreview from '../annotation/AnnotationPreview';
import isContentEmpty from '../utils/isContentEmpty';
import VersionChangeCard from './VersionChangeCard';

const ANNOTATION_FRAGMENT = gql(`
  fragment AnnotationVersionChangeCard_annotation on Annotation {
    id
    ...AnnotationPreview_annotation
  }
`);

export type AnnotationVersionChange<
  Annotation extends DocumentType<typeof ANNOTATION_FRAGMENT>,
> = {
  id: string;
  annotation: Annotation;
  type: 'annotation';
  isNew: boolean;
};

export const toAnnotationVersionChange = <
  Annotation extends DocumentType<typeof ANNOTATION_FRAGMENT>,
>(
  annotation: Annotation,
  { isNew }: { isNew: boolean } = { isNew: false },
): AnnotationVersionChange<typeof annotation> => ({
  id: annotation.id,
  annotation,
  type: 'annotation',
  isNew,
});

interface AnnotationVersionChangeCardProps<
  Annotation extends DocumentType<typeof ANNOTATION_FRAGMENT>,
> {
  focused: boolean;
  shouldOpenPopOver: boolean;
  versionChange: AnnotationVersionChange<Annotation>;
  onHighlight: () => void;
  onRemoveHighlight: () => void;
  onFocus: () => void;
  onCopyLink: () => void;
  magneticWallItemSize: MagneticWallItemSize;
}

const AnnotationVersionChangeCard = <
  Annotation extends DocumentType<typeof ANNOTATION_FRAGMENT>,
>({
  focused,
  shouldOpenPopOver,
  versionChange: { annotation, isNew },
  onHighlight,
  onRemoveHighlight,
  onFocus,
  onCopyLink,
  magneticWallItemSize,
}: AnnotationVersionChangeCardProps<Annotation>) => {
  const [isPopOverOpen, setIsPopOverOpen] = useState(false);

  useEffect(() => {
    if (shouldOpenPopOver) {
      setIsPopOverOpen(true);
    }
  }, [shouldOpenPopOver]);

  const { updateThreadHeights, layout, handlePopOverRender } =
    useMagneticWallLayout();

  useEffect(() => {
    updateThreadHeights();
  }, [focused, updateThreadHeights]);

  const trimmedLinkText = annotation.linkText?.trim() ?? '';

  const sanitizedLink = sanitizeAnnotationLink(annotation.link ?? '');
  const shouldShowLink = !isContentEmpty(annotation.content) && sanitizedLink;

  const handleClick = () => {
    onFocus();

    if (magneticWallItemSize === 'compact') {
      setIsPopOverOpen(true);
    }
  };

  const avatarWithNewBadge = (
    <VersionChangeCard.NewBadgeContainer showBadge={isNew}>
      <AnnotationIcon />
    </VersionChangeCard.NewBadgeContainer>
  );

  const fullAnnotation = (
    <>
      <AnnotationPreviewContainer onClick={handleClick}>
        <AnnotationPreview
          annotation={annotation}
          avatar={
            focused ? (
              <VersionChangeCard.CopyLinkButton onCopyLink={onCopyLink}>
                {avatarWithNewBadge}
              </VersionChangeCard.CopyLinkButton>
            ) : (
              avatarWithNewBadge
            )
          }
        />
      </AnnotationPreviewContainer>

      <AnimatePresence>
        {focused && shouldShowLink && (
          <OpacityMotionTransition key="link" transition={{ duration: 0.25 }}>
            <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
          </OpacityMotionTransition>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <MagneticWallItem.Root
      id={annotation.id}
      focused={focused}
      size={magneticWallItemSize}
      onMouseEnter={() => {
        onHighlight();
      }}
      onMouseLeave={() => {
        onRemoveHighlight();
      }}
      compactView={
        <MagneticWallItem.ResponsivePopover
          onClose={() => setIsPopOverOpen(false)}
          target={
            <VersionChangeCard.Root onClick={handleClick} focused={focused}>
              <VersionChangeCard.NewBadgeContainer showBadge={isNew}>
                <VersionChangeCard.CompactAvatarContainer>
                  <AnnotationIcon hoverable />
                </VersionChangeCard.CompactAvatarContainer>
              </VersionChangeCard.NewBadgeContainer>
            </VersionChangeCard.Root>
          }
          onPopOverHeightChange={handlePopOverRender.bind(null, annotation.id)}
          content={
            <PopOverContainer noPadding fullHeight>
              {fullAnnotation}
            </PopOverContainer>
          }
          compactPopOverSide={layout.compactPopOverSide}
          open={isPopOverOpen}
        />
      }
      fullView={
        <MagneticWallItem.Card focused={focused} focusable overlapped={false}>
          {fullAnnotation}
        </MagneticWallItem.Card>
      }
    />
  );
};

export default AnnotationVersionChangeCard;
